import { DeleteOutlined, DownOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Collapse,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Card from 'components/@extended/Card'
import Paper from 'components/@extended/Paper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { FC, useEffect, useState } from 'react'
import {
  ComponentSourceType,
  ComponentType,
  getCompositeFunction,
  type CreateMetricDTO,
  type NeededTargetMetric,
  type SearchNeededTargetMetricsParams,
} from 'types/components'
import { ThemeMode } from 'types/config'

dayjs.extend(utc)

import AnimateButton from 'components/@extended/AnimateButton'
import { TextField } from 'components/@extended/TextField'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentField from 'components/ComponentField'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import TrueFalseSelector from 'components/TrueFalseSelector'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { postWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useSearchNeededTargetMetrics } from 'hooks/useComponents'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import { usePeriods } from 'hooks/usePeriods'
import { useSnackbar } from 'notistack'
import { startTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import type { OrganizationCoreSetting } from 'types/organization'
import { createIdLabelMap, DATE_FORMAT } from 'types/periods'
import type { UserProfile } from 'types/user-profile'
import { getOrganizationLabelMap } from 'types/user-profile'

interface CompositeValue {
  label: string
  description: string
  value: string
  target?: string
  componentType?: ComponentType
}

interface CompositeValues {
  values: CompositeValue[]
}

const CreateMetrics: FC = () => {
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  const [isReviewMode, setIsReviewMode] = useState(false)
  const [metrics, setMetrics] = useState<NeededTargetMetric[]>([])
  const [coreSettingsMap, setCoreSettingsMap] = useState<Record<string, OrganizationCoreSetting>>({})
  const [limit, setLimit] = useState<number>(100)
  const excludeMetricsEntered = false
  const [createMetricDTOs, setCreateMetricDTOs] = useState<CreateMetricDTO[]>([])
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [compositeValues, setCompositeValues] = useState<Record<number, CompositeValues>>({})
  const [effectiveDate, setEffectiveDate] = useState<dayjs.Dayjs | null>(dayjs().utc())

  const theme = useTheme()
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const { purcentAppState } = useGlobalState()
  const rootOrgId = purcentAppState?.selectedOrganization?.rootOrganizationId || ''
  const { periods } = usePeriods(token!, rootOrgId)

  const searchParams: SearchNeededTargetMetricsParams = {
    organizationIds: selectedOrganizations,
    periodIds: selectedPeriods,
    limit,
    excludeMetricsEntered,
  }

  const { targets, isLoading: isLoadingTargets } = useSearchNeededTargetMetrics(
    token!,
    searchParams,
    selectedOrganizations.length > 0 && selectedPeriods.length > 0
  )

  const { coreSettings, isLoading: isLoadingCoreSettings } = useFetchOrganizationCoreSettings(
    selectedOrganizations,
    token!
  )

  const periodLabelMap: Record<string, string> = (periods?.periods || []).reduce(
    (acc, period) => ({
      ...acc,
      ...createIdLabelMap(period),
    }),
    {}
  )
  const organizationLabelMap: Record<string, string> = profile ? getOrganizationLabelMap(profile as UserProfile) : {}

  useEffect(() => {
    if (targets) {
      // Set blank newValue for each metric and parse sourceInfo if it's a string
      const metricsWithBlankNewValues = targets.map((target) => {
        // Parse sourceInfo if it's a string
        let parsedSourceInfo = target.sourceInfo
        if (typeof target.sourceInfo === 'string') {
          try {
            parsedSourceInfo = JSON.parse(target.sourceInfo)
          } catch (e) {
            console.error('Error parsing sourceInfo:', e)
          }
        }

        return {
          ...target,
          sourceInfo: parsedSourceInfo,
          newValue:
            target.currentActualValue !== undefined && target.currentActualValue !== null
              ? target.currentActualValue
              : target.componentType === ComponentType.COMPONENT_TYPE_BOOL
                ? 'false'
                : '',
        }
      })
      setMetrics(metricsWithBlankNewValues)

      // Initialize composite values for composite metrics
      const initialCompositeValues: Record<number, CompositeValues> = {}
      metricsWithBlankNewValues.forEach((metric, index) => {
        if (metric.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
          // Check if the target has composite structure (compositeTargetValues)
          let hasCompositeTarget = false
          let compositeTargetStructure: any = null

          try {
            const parsedTarget = JSON.parse(metric.value)
            if (parsedTarget.compositeTargetValues && Array.isArray(parsedTarget.compositeTargetValues)) {
              hasCompositeTarget = true
              compositeTargetStructure = parsedTarget.compositeTargetValues
            }
          } catch (e) {
            // Target is not composite structure
          }

          if (hasCompositeTarget && compositeTargetStructure) {
            // Initialize with target structure, but populate values from currentActualValue if available
            let existingValues: any = {}
            if (metric.currentActualValue) {
              try {
                const parsedActual = JSON.parse(metric.currentActualValue)
                if (parsedActual.values) {
                  // Create a map of existing values by label for easy lookup
                  existingValues = parsedActual.values.reduce((acc: any, item: any) => {
                    acc[item.label] = item.value
                    return acc
                  }, {})
                }
              } catch (e) {
                // Current actual value is not valid JSON
              }
            }

            // Create composite values based on target structure
            initialCompositeValues[index] = {
              values: compositeTargetStructure.map((targetItem: any) => ({
                label: targetItem.label,
                description: targetItem.description,
                value: existingValues[targetItem.label] || '0', // Use existing value or default to '0'
                target: targetItem.target,
                componentType: targetItem.componentType,
              })),
            }
          } else {
            // No composite target structure, use existing logic
            if (metric.currentActualValue) {
              try {
                initialCompositeValues[index] = JSON.parse(metric.currentActualValue)
              } catch (e) {
                initialCompositeValues[index] = { values: [] }
              }
            } else {
              initialCompositeValues[index] = { values: [] }
            }
          }

          // Update the metric's newValue with the JSON string of composite values
          if (initialCompositeValues[index]) {
            metricsWithBlankNewValues[index].newValue = JSON.stringify(initialCompositeValues[index])
          }
        }
      })
      setCompositeValues(initialCompositeValues)
      setMetrics(metricsWithBlankNewValues)
    }
  }, [targets])

  useEffect(() => {
    if (coreSettings && coreSettings.length > 0) {
      const newCoreSettingsMap = coreSettings.reduce(
        (acc, setting) => {
          acc[setting.organizationId] = setting
          return acc
        },
        {} as Record<string, OrganizationCoreSetting>
      )
      setCoreSettingsMap(newCoreSettingsMap)
    }
  }, [coreSettings])

  const handleOrganizationChange = (newOrganizations: string[]) => {
    setSelectedOrganizations(newOrganizations)
  }

  const handlePeriodChange = (newPeriods: string[]) => {
    setSelectedPeriods(newPeriods)
  }

  const handleMetricChange = (index: number, newValue: any) => {
    const updatedMetrics = [...metrics]

    // For boolean components, ensure we never have empty strings
    if (updatedMetrics[index].componentType === ComponentType.COMPONENT_TYPE_BOOL) {
      updatedMetrics[index].newValue =
        newValue === '' || newValue === null || newValue === undefined ? 'false' : newValue
    } else {
      updatedMetrics[index].newValue = newValue
    }

    setMetrics(updatedMetrics)
  }

  const handleDeleteMetric = (index: number) => {
    const updatedMetrics = metrics.filter((_, i) => i !== index)
    setMetrics(updatedMetrics)

    // Update composite values to maintain correct indices
    const updatedCompositeValues: Record<number, CompositeValues> = {}
    updatedMetrics.forEach((metric, newIndex) => {
      if (metric.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        // Find the original index of this metric
        const originalIndex = metrics.findIndex(
          (m) =>
            m.componentId === metric.componentId &&
            m.organizationId === metric.organizationId &&
            m.periodId === metric.periodId
        )
        if (originalIndex !== -1 && compositeValues[originalIndex]) {
          updatedCompositeValues[newIndex] = compositeValues[originalIndex]
        }
      }
    })
    setCompositeValues(updatedCompositeValues)
  }

  const handleReview = () => {
    // Filter metrics to only include those where a value was entered
    const filteredMetrics = metrics.filter((metric, index) => {
      // For composite metrics, check if there are values
      if (metric.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        return compositeValues[index]?.values?.length > 0
      }

      // For other metrics, check if newValue is not empty/null/undefined after trimming
      if (metric.newValue === null || metric.newValue === undefined) {
        return false
      }

      const trimmedValue = typeof metric.newValue === 'string' ? metric.newValue.trim() : metric.newValue

      // Filter out empty strings
      if (trimmedValue === '') {
        return false
      }

      // For numeric component types, check if the value would evaluate to NaN
      if (
        metric.componentType === ComponentType.COMPONENT_TYPE_INTEGER ||
        metric.componentType === ComponentType.COMPONENT_TYPE_FLOAT ||
        metric.componentType === ComponentType.COMPONENT_TYPE_CURRENCY ||
        metric.componentType === ComponentType.COMPONENT_TYPE_PERCENTAGE
      ) {
        const numericValue = typeof trimmedValue === 'string' ? parseFloat(trimmedValue) : Number(trimmedValue)
        if (isNaN(numericValue)) {
          return false
        }
      }

      return true
    })

    // Create DTOs only for metrics with values
    const newCreateMetricDTOs = filteredMetrics.map((metric) => ({
      componentId: metric.componentId,
      organizationId: metric.organizationId,
      rootOrganizationId: metric.rootOrganizationId,
      periodId: metric.periodId,
      rootPeriodId: metric.rootPeriodId,
      userId: metric.userId,
      value: metric.newValue as string,
    }))
    setCreateMetricDTOs(newCreateMetricDTOs)
    setIsReviewMode(true)
  }

  const handleBack = () => {
    setIsReviewMode(false)
    setCreateMetricDTOs([])
    setEffectiveDate(null)
  }

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleSubmit = () => {
    if (!effectiveDate) {
      enqueueSnackbar('Please select an effective date for the actuals', { variant: 'error' })
      return
    }

    // Allow past dates for recording actuals

    const metricsToSubmit = createMetricDTOs.map((dto) => {
      // Find the original metric to check component type
      const originalMetric = metrics.find(
        (m) => m.componentId === dto.componentId && m.periodId === dto.periodId && m.userId === dto.userId
      )

      // For boolean components, convert empty strings to 'false'
      let finalValue = dto.value
      if (originalMetric?.componentType === ComponentType.COMPONENT_TYPE_BOOL && dto.value === '') {
        finalValue = 'false'
      }

      const dateString = effectiveDate.format('YYYY-MM-DD')
      const isoString = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')

      return {
        componentId: dto.componentId,
        rootOrganizationId: dto.rootOrganizationId,
        organizationId: dto.organizationId,
        rootPeriodId: dto.rootPeriodId,
        periodId: dto.periodId,
        userId: dto.userId,
        value: finalValue,
        effectiveDate: isoString,
      }
    })

    postWithToken('components/metrics', token!, metricsToSubmit)
      .then((response) => {
        enqueueSnackbar('Actuals submitted successfully', { variant: 'success' })
        // Reset the form
        setIsReviewMode(false)
        setSelectedOrganizations([])
        setSelectedPeriods([])
        setMetrics([])
        setCreateMetricDTOs([])
        setEffectiveDate(null)
      })
      .catch((error) => {
        const errorMessage = error.message || 'Failed to submit metrics'
        enqueueSnackbar(errorMessage, { variant: 'error' })
      })
      .finally(() => startTransition(() => navigate('/admin/components/actuals')))
  }

  const toggleRowExpansion = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const handleAddCompositeValue = (index: number) => {
    const currentValues = compositeValues[index] || { values: [] }
    const newCompositeValues = {
      ...compositeValues,
      [index]: {
        values: [
          ...currentValues.values,
          {
            label: `Value ${currentValues.values.length + 1}`,
            description: 'Enter description',
            value: '0',
            target: '',
            componentType: metrics[index]?.componentType || ComponentType.COMPONENT_TYPE_INTEGER,
          },
        ],
      },
    }
    setCompositeValues(newCompositeValues)

    // Update the metric's newValue with the JSON string
    const updatedMetrics = [...metrics]
    updatedMetrics[index].newValue = JSON.stringify(newCompositeValues[index])
    setMetrics(updatedMetrics)
  }

  const handleRemoveCompositeValue = (metricIndex: number, valueIndex: number) => {
    if (!compositeValues[metricIndex]) return

    const newValues = {
      ...compositeValues,
      [metricIndex]: {
        values: compositeValues[metricIndex].values.filter((_, i) => i !== valueIndex),
      },
    }
    setCompositeValues(newValues)

    // Update the metric's newValue with the JSON string
    const updatedMetrics = [...metrics]
    updatedMetrics[metricIndex].newValue = JSON.stringify(newValues[metricIndex])
    setMetrics(updatedMetrics)
  }

  const handleUpdateCompositeValue = (
    metricIndex: number,
    valueIndex: number,
    field: 'label' | 'description' | 'value' | 'target',
    newValue: string
  ) => {
    if (!compositeValues[metricIndex]) return

    const newValues = {
      ...compositeValues,
      [metricIndex]: {
        values: compositeValues[metricIndex].values.map((item: CompositeValue, i: number) =>
          i === valueIndex ? { ...item, [field]: newValue } : item
        ),
      },
    }
    setCompositeValues(newValues)

    // Update the metric's newValue with the JSON string
    const updatedMetrics = [...metrics]
    updatedMetrics[metricIndex].newValue = JSON.stringify(newValues[metricIndex])
    setMetrics(updatedMetrics)
  }

  const handleUpdateComponentType = (metricIndex: number, valueIndex: number, newType: ComponentType) => {
    if (!compositeValues[metricIndex]) return

    const newValues = {
      ...compositeValues,
      [metricIndex]: {
        values: compositeValues[metricIndex].values.map((item: CompositeValue, i: number) =>
          i === valueIndex ? { ...item, componentType: newType } : item
        ),
      },
    }
    setCompositeValues(newValues)

    // Update the metric's newValue with the JSON string
    const updatedMetrics = [...metrics]
    updatedMetrics[metricIndex].newValue = JSON.stringify(newValues[metricIndex])
    setMetrics(updatedMetrics)
  }

  const hasCompositeTargetStructure = (metric: NeededTargetMetric): boolean => {
    try {
      const parsedTarget = JSON.parse(metric.value)
      return (
        parsedTarget.compositeTargetValues &&
        Array.isArray(parsedTarget.compositeTargetValues) &&
        parsedTarget.compositeTargetValues.length > 0
      )
    } catch (e) {
      return false
    }
  }

  const renderOrgPeriodConfig = () => {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Team & Period Selections
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              {isLoadingProfile ? (
                <CircularProgress />
              ) : (
                <MultiOrganizationSelector
                  userProfile={profile!}
                  selectedOrganizationIds={selectedOrganizations}
                  handleChange={handleOrganizationChange}
                  label="Select Teams"
                  variant="outlined"
                  labelBackgroundColor={theme.palette.background.paper}
                  selectAll={true}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              {!periods || !periods.periods || periods.periods.length === 0 ? (
                <CircularProgress />
              ) : (
                <MultiPeriodSelector
                  periods={periods.periods}
                  selectedPeriods={selectedPeriods}
                  handleChange={handlePeriodChange}
                  label="Select Periods"
                  variant="outlined"
                  shrinkLabel
                  labelBackgroundColor={theme.palette.background.paper}
                  disableClosed={true}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 1 }} sx={{ mt: -0.25 }}>
              <Typography variant="caption">Limit</Typography>
              <Select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                fullWidth
                size="small"
                variant="standard"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  }

  const renderMetricsList = () => {
    return (
      <>
        {isLoadingTargets || isLoadingCoreSettings ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ position: 'sticky !important' }}>Label</TableCell>
                  <TableCell sx={{ position: 'sticky !important' }}>Team / Employee</TableCell>
                  <TableCell sx={{ position: 'sticky !important' }}>Period</TableCell>
                  <TableCell sx={{ position: 'sticky !important' }}>Target</TableCell>
                  <TableCell sx={{ position: 'sticky !important' }}>Value</TableCell>
                  <TableCell sx={{ position: 'sticky !important' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.map((metric, index) => {
                  const orgSettings = coreSettingsMap[metric.organizationId] || {
                    currency: 'USD',
                    currencyDecimals: 0,
                  }
                  const teamOrEmployee =
                    metric.firstName && metric.lastName
                      ? `${metric.firstName} ${metric.lastName}`
                      : metric.organizationId && organizationLabelMap[metric.organizationId]

                  const isComposite = metric.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
                  const isExpanded = expandedRows[index] || false

                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{metric.label}</TableCell>
                        <TableCell>{teamOrEmployee}</TableCell>
                        <TableCell>{metric.periodId && periodLabelMap[metric.periodId]}</TableCell>
                        <TableCell>
                          <ComponentDisplay
                            componentType={metric.componentType as ComponentType}
                            value={metric.value}
                            currency={orgSettings.currency}
                            currencyDecimals={orgSettings.currencyDecimals}
                            numericDecimalPlaces={metric.numericDecimalPlaces}
                            sourceType={metric.sourceType}
                            compositeFunction={getCompositeFunction(metric.sourceInfo)}
                            compositeType={metric.sourceInfo?.compositeType}
                            lowerIsBetter={metric.lowerIsBetter}
                            componentLabel={metric.label}
                            periodLabel={periodLabelMap[metric.periodId]}
                            isTarget={true}
                          />
                        </TableCell>
                        <TableCell>
                          {isComposite ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: 2 }}>
                              <ComponentDisplay
                                componentType={metric.componentType as ComponentType}
                                value={metric.newValue || '{"values":[]}'}
                                currency={orgSettings.currency}
                                currencyDecimals={orgSettings.currencyDecimals}
                                numericDecimalPlaces={metric.numericDecimalPlaces}
                                sourceType={metric.sourceType}
                                compositeFunction={getCompositeFunction(metric.sourceInfo)}
                                compositeType={metric.sourceInfo?.compositeType}
                                lowerIsBetter={metric.lowerIsBetter}
                              />
                              <IconButton size="small" onClick={() => toggleRowExpansion(index)} sx={{ marginLeft: 2 }}>
                                {isExpanded ? <UpOutlined /> : <DownOutlined />}
                              </IconButton>
                            </Box>
                          ) : metric.componentType === ComponentType.COMPONENT_TYPE_BOOL ? (
                            <TrueFalseSelector
                              value={metric.newValue === 'true'}
                              onChange={(value) => handleMetricChange(index, value === null ? '' : value.toString())}
                              name={`metric-${index}`}
                              variant="outlined"
                              pill
                              shrink
                              style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                            />
                          ) : (
                            <ComponentField
                              componentType={metric.componentType as ComponentType}
                              label=""
                              value={metric.newValue || ''}
                              onChange={(value) => handleMetricChange(index, value)}
                              name={`metric-${index}`}
                              currency={orgSettings.currency}
                              currencyDecimals={orgSettings.currencyDecimals}
                              decimalPlaces={metric.numericDecimalPlaces ?? 2}
                              sourceType={metric.sourceType}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => handleDeleteMetric(index)} size="small">
                            <DeleteOutlined />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expandable row for composite metrics */}
                      {isComposite && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ padding: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                  p: 1,
                                  mt: 2,
                                  mb: 4,
                                  backgroundColor: theme.palette.background.paper,
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: 2 }}>
                                  <Typography variant="h5">Composite Values for {metric.label}</Typography>
                                  {!hasCompositeTargetStructure(metric) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <Button
                                        startIcon={<PlusOutlined />}
                                        size="small"
                                        onClick={() => handleAddCompositeValue(index)}
                                      >
                                        Add Value
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell width="20%">Label</TableCell>
                                        <TableCell width="50%">Description</TableCell>
                                        <TableCell width="10%">Type</TableCell>
                                        <TableCell width="10%">Target</TableCell>
                                        <TableCell width="10%">Value</TableCell>
                                        <TableCell width="10%" align="center">
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {compositeValues[index]?.values.map((item, valueIndex) => {
                                        const hasTargetStructure = hasCompositeTargetStructure(metric)

                                        return (
                                          <TableRow key={valueIndex}>
                                            <TableCell>
                                              <TextField
                                                value={item.label}
                                                onChange={(e) =>
                                                  handleUpdateCompositeValue(index, valueIndex, 'label', e.target.value)
                                                }
                                                fullWidth
                                                variant="outlined"
                                                disabled={hasTargetStructure}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <TextField
                                                value={item.description}
                                                onChange={(e) =>
                                                  handleUpdateCompositeValue(
                                                    index,
                                                    valueIndex,
                                                    'description',
                                                    e.target.value
                                                  )
                                                }
                                                fullWidth
                                                variant="outlined"
                                                disabled={hasTargetStructure}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Select
                                                value={item.componentType || metric.componentType}
                                                onChange={(e) =>
                                                  handleUpdateComponentType(
                                                    index,
                                                    valueIndex,
                                                    e.target.value as ComponentType
                                                  )
                                                }
                                                fullWidth
                                                variant="outlined"
                                                disabled={
                                                  hasTargetStructure ||
                                                  (metric.componentType as ComponentType) !==
                                                    ComponentType.COMPONENT_TYPE_PERCENTAGE
                                                }
                                                sx={{
                                                  '& .MuiMenuItem-root:hover': {
                                                    backgroundColor:
                                                      theme.palette.mode === ThemeMode.DARK
                                                        ? theme.palette.primary.darker
                                                        : theme.palette.primary.lighter,
                                                  },
                                                  '& .MuiMenuItem-root.Mui-selected:hover': {
                                                    backgroundColor: theme.palette.action.selected,
                                                  },
                                                  fontSize: 'inherit',
                                                  borderRadius: '21px',
                                                }}
                                              >
                                                <MenuItem value={ComponentType.COMPONENT_TYPE_INTEGER}>
                                                  Integer
                                                </MenuItem>
                                                <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>
                                                  Currency
                                                </MenuItem>
                                                <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>
                                                  Percentage
                                                </MenuItem>
                                                <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                                                <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                                              </Select>
                                            </TableCell>
                                            <TableCell>
                                              {
                                                <Tooltip
                                                  title={
                                                    hasTargetStructure
                                                      ? 'Target is predefined from composite target structure'
                                                      : 'This field is only available for percentage components'
                                                  }
                                                >
                                                  <div>
                                                    <ComponentField
                                                      componentType={
                                                        item.componentType || (metric.componentType as ComponentType)
                                                      }
                                                      label=""
                                                      value={item.target || ''}
                                                      onChange={(value) =>
                                                        handleUpdateCompositeValue(index, valueIndex, 'target', value)
                                                      }
                                                      name={`metric-${index}-composite-target-${valueIndex}`}
                                                      currency={orgSettings.currency}
                                                      currencyDecimals={orgSettings.currencyDecimals}
                                                      decimalPlaces={metric.numericDecimalPlaces ?? 2}
                                                      disabled={true}
                                                    />
                                                  </div>
                                                </Tooltip>
                                              }
                                            </TableCell>
                                            <TableCell>
                                              {(item.componentType || metric.componentType) ===
                                              ComponentType.COMPONENT_TYPE_BOOL ? (
                                                <TrueFalseSelector
                                                  value={item.value === 'true'}
                                                  onChange={(value) =>
                                                    handleUpdateCompositeValue(
                                                      index,
                                                      valueIndex,
                                                      'value',
                                                      value === null ? '' : value.toString()
                                                    )
                                                  }
                                                  name={`metric-${index}-composite-${valueIndex}`}
                                                  variant="outlined"
                                                  pill
                                                  shrink
                                                  style={{ minWidth: 180, maxWidth: 220, width: '100%' }}
                                                />
                                              ) : (
                                                <ComponentField
                                                  componentType={
                                                    item.componentType || (metric.componentType as ComponentType)
                                                  }
                                                  label=""
                                                  value={item.value}
                                                  onChange={(value) =>
                                                    handleUpdateCompositeValue(index, valueIndex, 'value', value)
                                                  }
                                                  name={`metric-${index}-composite-${valueIndex}`}
                                                  currency={orgSettings.currency}
                                                  currencyDecimals={orgSettings.currencyDecimals}
                                                  decimalPlaces={metric.numericDecimalPlaces ?? 2}
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell width="10%" align="center">
                                              {!hasTargetStructure && (
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleRemoveCompositeValue(index, valueIndex)}
                                                >
                                                  <DeleteOutlined />
                                                </IconButton>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      })}
                                      {(!compositeValues[index] || compositeValues[index].values.length === 0) && (
                                        <TableRow>
                                          <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="textSecondary">
                                              No values added yet. Click "Add Value" to begin.
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    )
  }

  const renderReviewConfig = () => {
    return (
      <>
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Team / Employee</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {createMetricDTOs.map((dto, index) => {
                  const metric = metrics.find(
                    (m) => m.componentId === dto.componentId && m.periodId === dto.periodId && m.userId === dto.userId
                  )
                  if (!metric) return null

                  // Parse sourceInfo if it's a string
                  if (typeof metric.sourceInfo === 'string') {
                    metric.sourceInfo = JSON.parse(metric.sourceInfo)
                  }

                  const teamOrEmployee =
                    metric.firstName && metric.lastName
                      ? `${metric.firstName} ${metric.lastName}`
                      : metric.organizationId && organizationLabelMap[metric.organizationId]

                  const orgSettings = coreSettingsMap[metric.organizationId] || {
                    currency: 'USD',
                    currencyDecimals: 0,
                  }

                  return (
                    <TableRow key={index}>
                      <TableCell>{metric.label}</TableCell>
                      <TableCell>{teamOrEmployee}</TableCell>
                      <TableCell>{metric.periodId && periodLabelMap[metric.periodId]}</TableCell>
                      <TableCell>
                        <ComponentDisplay
                          componentType={metric.componentType as ComponentType}
                          value={metric.value}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          numericDecimalPlaces={metric.numericDecimalPlaces}
                          sourceType={metric.sourceType}
                          compositeFunction={getCompositeFunction(metric.sourceInfo)}
                          componentLabel={metric.label}
                          periodLabel={periodLabelMap[metric.periodId]}
                          isTarget={true}
                        />
                      </TableCell>
                      <TableCell>
                        <ComponentDisplay
                          componentType={metric.componentType as ComponentType}
                          value={dto.value}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          numericDecimalPlaces={metric.numericDecimalPlaces}
                          sourceType={metric.sourceType}
                          compositeFunction={getCompositeFunction(metric.sourceInfo)}
                          compositeType={metric.sourceInfo?.compositeType}
                          lowerIsBetter={metric.lowerIsBetter}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    )
  }

  return (
    <>
      <Typography variant="h3" gutterBottom>
        {isReviewMode ? 'Review Actuals' : 'Create Actuals'}
      </Typography>
      {!isReviewMode && renderOrgPeriodConfig()}
      {!isReviewMode && renderMetricsList()}
      {!isReviewMode ? (
        <Box mt={3}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <AnimateButton>
              <Button
                disabled={isLoadingTargets}
                variant="contained"
                color="warning"
                onClick={() => {
                  startTransition(() => {
                    navigate('/admin/components/actuals')
                  })
                }}
              >
                Cancel
              </Button>
            </AnimateButton>
            <Button variant="contained" onClick={handleReview} disabled={isLoadingTargets}>
              Review
            </Button>
          </Stack>
        </Box>
      ) : (
        renderReviewConfig()
      )}
      {isReviewMode && (
        <Box mt={3}>
          <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Effective Date
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select the date when these actuals should take effect
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Effective Date *"
                value={effectiveDate}
                onChange={(newValue) => {
                  if (newValue) {
                    const dateString = newValue.format('YYYY-MM-DD')
                    const utcDate = dayjs.utc(dateString)
                    setEffectiveDate(utcDate)
                  } else {
                    setEffectiveDate(null)
                  }
                }}
                format={DATE_FORMAT}
                // Allow any date for recording actuals
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    error: !effectiveDate,
                    helperText: !effectiveDate ? 'Effective date is required' : '',
                    InputLabelProps: {
                      shrink: true,
                      sx: {
                        '&.Mui-focused': {
                          color: (theme) => theme.palette.secondary.main,
                        },
                      },
                    },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                        '& fieldset': {
                          borderColor: (theme) => theme.palette.primary.main,
                        },
                        '&:hover fieldset': {
                          borderColor: (theme) => theme.palette.primary.dark,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: (theme) => theme.palette.secondary.main,
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Card>

          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <AnimateButton>
              <Button
                disabled={isLoadingTargets}
                variant="contained"
                color="warning"
                onClick={() => {
                  startTransition(() => {
                    navigate('/admin/components/actuals')
                  })
                }}
              >
                Cancel
              </Button>
            </AnimateButton>

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
              <Button variant="contained" onClick={handleSubmit}>
                Submit Actuals
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  )
}

export default CreateMetrics
